<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: v =>
      ['text', 'multiline', 'number', 'currency', 'date', 'datetime'].includes(
        v
      ),
  },
  placeholder: {
    type: String,
    default: '',
  },
  validator: {
    type: Function,
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  displayClass: {
    type: String,
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
  currencySymbol: {
    type: String,
    default: 'R$',
  },
  currencyLocale: {
    type: String,
    default: 'pt-BR',
  },
  emptyLabel: {
    type: String,
    default: '—',
  },
});

const emit = defineEmits(['update:modelValue', 'save', 'cancel']);

const { t } = useI18n();

const editing = ref(false);
const inputRef = ref(null);
const localValue = ref(props.modelValue);
const validationError = ref('');

watch(
  () => props.modelValue,
  val => {
    if (!editing.value) localValue.value = val;
  }
);

// ── Formatting ──────────────────────────────────────────────────────────────

const formatCurrency = val => {
  const num = parseFloat(String(val).replace(',', '.'));
  if (Number.isNaN(num)) return props.emptyLabel;
  return new Intl.NumberFormat(props.currencyLocale, {
    style: 'currency',
    currency: 'BRL',
    currencyDisplay: 'symbol',
  })
    .format(num)
    .replace('R$', props.currencySymbol);
};

const displayValue = computed(() => {
  const v = props.modelValue;
  if (v === null || v === undefined || v === '') return '';
  if (props.type === 'currency') return formatCurrency(v);
  return String(v);
});

const isEmpty = computed(() => displayValue.value === '');

// ── Multiline auto-grow (must be defined before startEdit) ──────────────────

const growTextarea = () => {
  const el = inputRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

// ── Enter edit mode ──────────────────────────────────────────────────────────

const startEdit = () => {
  if (props.disabled || props.saving) return;
  localValue.value = props.modelValue;
  validationError.value = '';
  editing.value = true;
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus();
      if (props.type === 'multiline') growTextarea();
    }
  });
};

// ── Validation & save ────────────────────────────────────────────────────────

const coercedValue = computed(() => {
  if (props.type === 'number') return Number(localValue.value) || 0;
  if (props.type === 'currency') {
    return parseFloat(String(localValue.value).replace(',', '.')) || 0;
  }
  return localValue.value;
});

const commitSave = () => {
  if (props.validator) {
    const result = props.validator(coercedValue.value);
    if (result !== true) {
      validationError.value =
        typeof result === 'string'
          ? result
          : t('PIPELINE.INLINE_EDIT.VALIDATION_ERROR');
      return;
    }
  }
  validationError.value = '';
  editing.value = false;
  emit('update:modelValue', coercedValue.value);
  emit('save', coercedValue.value);
};

const cancelEdit = () => {
  editing.value = false;
  localValue.value = props.modelValue;
  validationError.value = '';
  emit('cancel');
};

// ── Keyboard handlers ────────────────────────────────────────────────────────

const onKeydown = e => {
  if (e.key === 'Escape') {
    cancelEdit();
    return;
  }
  if (e.key === 'Enter' && props.type !== 'multiline') {
    e.preventDefault();
    commitSave();
  }
};

const onBlur = () => {
  // Small delay so a click on popover/tooltip doesn't conflict
  setTimeout(() => {
    if (editing.value) commitSave();
  }, 120);
};

// ── Display key handler ──────────────────────────────────────────────────────

const onDisplayKeydown = e => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    startEdit();
  }
};

// ── Input native type ────────────────────────────────────────────────────────

const nativeInputType = computed(() => {
  if (props.type === 'number' || props.type === 'currency') return 'text';
  if (props.type === 'date') return 'date';
  if (props.type === 'datetime') return 'datetime-local';
  return 'text';
});
</script>

<template>
  <div class="relative inline-block w-full group/inline-field">
    <!-- ── DISPLAY mode ── -->
    <template v-if="!editing">
      <div
        :role="disabled ? undefined : 'button'"
        :tabindex="disabled ? undefined : 0"
        :aria-label="
          isEmpty ? t('PIPELINE.INLINE_EDIT.EMPTY_VALUE') : displayValue
        "
        class="flex items-center gap-1 min-h-[28px] cursor-pointer rounded px-1 -mx-1 hover:bg-n-alpha-1 transition-colors duration-100 select-none"
        :class="[
          displayClass,
          { 'opacity-50 cursor-default pointer-events-none': disabled },
        ]"
        @click="startEdit"
        @keydown="onDisplayKeydown"
      >
        <!-- Pre-line for multiline -->
        <span
          v-if="type === 'multiline' && !isEmpty"
          class="whitespace-pre-line text-n-slate-12 text-sm leading-snug flex-1"
          >{{ displayValue }}</span
        >

        <!-- Single-line or empty -->
        <span
          v-else
          class="text-sm leading-snug flex-1"
          :class="isEmpty ? 'text-n-slate-10 italic' : 'text-n-slate-12'"
        >
          {{ isEmpty ? emptyLabel : displayValue }}
        </span>

        <!-- Edit pencil icon (visible on hover unless disabled) -->
        <span
          v-if="!disabled"
          class="opacity-0 group-hover/inline-field:opacity-60 transition-opacity duration-100 text-n-slate-11 flex-shrink-0"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 1 2-2v-7"
            />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </span>
      </div>
    </template>

    <!-- ── EDIT mode ── -->
    <template v-else>
      <div class="relative">
        <!-- Multiline textarea -->
        <textarea
          v-if="type === 'multiline'"
          ref="inputRef"
          v-model="localValue"
          :placeholder="placeholder || t('PIPELINE.INLINE_EDIT.SAVE_HINT')"
          :disabled="saving"
          rows="2"
          class="w-full resize-none overflow-hidden rounded-md px-2 py-1 text-sm text-n-slate-12 bg-n-solid-2 border border-n-weak focus:border-n-brand focus:ring-1 focus:ring-n-brand/20 focus:outline-none disabled:opacity-50"
          :class="inputClass"
          @keydown="onKeydown"
          @blur="onBlur"
          @input="growTextarea"
        />

        <!-- Single-line input -->
        <input
          v-else
          ref="inputRef"
          v-model="localValue"
          :type="nativeInputType"
          :placeholder="placeholder"
          :disabled="saving"
          class="w-full rounded-md px-2 py-1 text-sm text-n-slate-12 bg-n-solid-2 border border-n-weak focus:border-n-brand focus:ring-1 focus:ring-n-brand/20 focus:outline-none disabled:opacity-50"
          :class="inputClass"
          @keydown="onKeydown"
          @blur="onBlur"
        />

        <!-- Saving spinner -->
        <span
          v-if="saving"
          class="absolute right-2 top-1/2 -translate-y-1/2"
          :aria-label="t('PIPELINE.INLINE_EDIT.SAVING')"
        >
          <svg
            class="animate-spin h-3.5 w-3.5 text-n-brand"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        </span>
      </div>

      <!-- Validation error tooltip -->
      <p
        v-if="validationError"
        role="alert"
        class="mt-1 text-xs text-ruby-9 font-medium"
      >
        {{ validationError }}
      </p>

      <!-- Hint -->
      <p v-else class="mt-0.5 text-xs text-n-slate-10">
        {{ t('PIPELINE.INLINE_EDIT.SAVE_HINT') }}
      </p>
    </template>
  </div>
</template>
